import { virtualMachinesListService } from '@/components/virtual-machines-list/services/VirtualMachinesListService';
import { virtualMachineDetailsService } from '@/components/virtual-machine-edit/services/VirtualMachineDetailsService';
import EventBus from '@/EventBus';
import deepMerge from '@/tools/deepMerge';
import { websocketService } from '@/services/WebsocketService';

const state = () => ({
  virtualMachines: {},
  sortBy: 'created_at',
  virtualMachineHardware: {},
});

const mutations = {
  setMachines(state: any, data: { data: any; workspaceId: any; regionId: any }) {
    if (!state.virtualMachines[data.workspaceId]) {
      state.virtualMachines[data.workspaceId] = {};
    }
    data.data.sort((a: any, b: any) => {
      if (state.sortBy === 'created_at') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else if (state.sortBy === 'name_ascending') {
        return a.name.localeCompare(b.name);
      } else if (state.sortBy === 'name_descending') {
        return b.name.localeCompare(a.name);
      } else {
        return a[state.sortBy] - b[state.sortBy];
      }
    });
    state.virtualMachines[data.workspaceId][data.regionId] = data.data;
    state.virtualMachines = JSON.parse(JSON.stringify(state.virtualMachines));
  },
  setSort(state: any, sortBy: string) {
    state.sortBy = sortBy;
  },
  setHardware(state: any, data: any) {
    if (data && data.id) {
      state.virtualMachineHardware[data.id] = state.virtualMachineHardware[data.id]
        ? deepMerge(state.virtualMachineHardware[data.id], data.data)
        : { ...data.data };
      state.virtualMachineHardware = { ...state.virtualMachineHardware };
    }
  },
};

const actions = {
  getMachines({ state, commit, rootState }: any, forceUpdate: boolean = false) {
    if (
      state.virtualMachines[rootState.currentWorkspace.id] &&
      state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId] &&
      !forceUpdate &&
      websocketService.isConnected
    ) {
      commit('setMachines', {
        data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
        workspaceId: rootState.currentWorkspace.id,
        regionId: rootState.currentWorkspace.currentRegionId,
      });
      return new Promise(resolve => {
        resolve();
      });
    }
    return virtualMachinesListService
      .getVirtualMachines(rootState.currentWorkspace.id, rootState.currentWorkspace.currentRegionId)
      .then(data => {
        commit('setMachines', {
          data: data.virtual_machines,
          workspaceId: rootState.currentWorkspace.id,
          regionId: rootState.currentWorkspace.currentRegionId,
        });
      })
      .catch(() => {});
  },
  getMachinesForWorkspace({ state, commit, rootState }: any, data: any) {
    if (
      state.virtualMachines[data.workspaceId] &&
      state.virtualMachines[data.workspaceId][data.regionId] &&
      !data.forceUpdate &&
      websocketService.isConnected
    ) {
      commit('setMachines', {
        data: state.virtualMachines[data.workspaceId][data.regionId],
        workspaceId: rootState.currentWorkspace.id,
        regionId: rootState.currentWorkspace.currentRegionId,
      });
      return new Promise(resolve => {
        resolve();
      });
    }
    return virtualMachinesListService.getVirtualMachines(data.workspaceId, data.regionId).then(responseData => {
      commit('setMachines', {
        data: responseData.virtual_machines,
        workspaceId: data.workspaceId,
        regionId: data.regionId,
      });
    });
  },
  getMachine({ state, commit, rootState, dispatch }: any, options: { machineId: number; forceUpdate: boolean }) {
    function getMachineById() {
      let index = -1;
      if (
        !!state.virtualMachines[rootState.currentWorkspace.id] &&
        !!state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
      ) {
        index = state.virtualMachines[rootState.currentWorkspace.id][
          rootState.currentWorkspace.currentRegionId
        ].findIndex((item: any) => item.id === options.machineId);
      }
      if (
        index !== -1 &&
        state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId][index]
          .operating_system &&
        !options.forceUpdate &&
        websocketService.isConnected
      ) {
        return;
      }
      return virtualMachineDetailsService
        .getVirtualMachineDetails(
          rootState.currentWorkspace.id,
          rootState.currentWorkspace.currentRegionId,
          options.machineId
        )
        .then(data => {
          if (index === -1) {
            EventBus.$emit('show-snackbar', 'Store error - machine not found');
            return;
          }
          state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId][
            index
          ] = data;
          commit('setMachines', {
            data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
            workspaceId: rootState.currentWorkspace.id,
            regionId: rootState.currentWorkspace.currentRegionId,
          });
        });
    }
    if (
      !state.virtualMachines[rootState.currentWorkspace.id] ||
      !state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
    ) {
      dispatch('getMachines').then(() => {
        getMachineById();
      });
    } else {
      getMachineById();
    }
  },
  getMachineHardware({ state, commit, rootState }: any, options: { machineId: number; forceUpdate: boolean }) {
    return new Promise(resolve => {
      if (state.virtualMachineHardware[options.machineId] && !options.forceUpdate && websocketService.isConnected) {
        commit('setHardware', { id: options.machineId, data: state.virtualMachineHardware[options.machineId] });
        resolve(state.virtualMachineHardware[options.machineId]);
      } else {
        return virtualMachineDetailsService
          .getHardware(rootState.currentWorkspace.id, rootState.currentWorkspace.currentRegionId, options.machineId)
          .then((data: any) => {
            commit('setHardware', { id: options.machineId, data });
            resolve(data);
          });
      }
    });
  },
  updateMachineHardware({ state, commit, rootState }: any, payload: any) {
    return new Promise(resolve => {
      if (!payload.id) {
        resolve(null);
        return;
      }
      if (state.virtualMachineHardware[payload.id] && payload.ram) {
        commit('setHardware', { id: payload.id, data: payload });
        resolve(payload);
      } else {
        virtualMachineDetailsService
          .getHardware(rootState.currentWorkspace.id, rootState.currentWorkspace.currentRegionId, payload.id)
          .then((data: any) => {
            commit('setHardware', { id: payload.id, data: data });
            resolve(data);
          });
      }
    });
  },
  sortMachines({ state, commit, rootState }: any, sortBy: string) {
    if (
      !state.virtualMachines[rootState.currentWorkspace.id] ||
      !state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
    ) {
      return;
    }
    commit('setSort', sortBy);
    commit('setMachines', {
      data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
      workspaceId: rootState.currentWorkspace.id,
      regionId: rootState.currentWorkspace.currentRegionId,
    });
  },
  addMachine({ state, commit, rootState }: any, machine: any) {
    if (
      !state.virtualMachines[rootState.currentWorkspace.id] ||
      !state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
    ) {
      return;
    }
    state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId].push(machine);
    commit('setMachines', {
      data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
      workspaceId: rootState.currentWorkspace.id,
      regionId: rootState.currentWorkspace.currentRegionId,
    });
  },
  destroyMachine({ state, commit, rootState }: any, payload: any) {
    if (
      !state.virtualMachines[rootState.currentWorkspace.id] ||
      !state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
    ) {
      return;
    }
    const index = state.virtualMachines[rootState.currentWorkspace.id][
      rootState.currentWorkspace.currentRegionId
    ].findIndex((item: any) => item.id === payload.id);
    if (index !== -1) {
      state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId].splice(index, 1);
    }
    commit('setMachines', {
      data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
      workspaceId: rootState.currentWorkspace.id,
      regionId: rootState.currentWorkspace.currentRegionId,
    });
  },
  updateMachineState({ state, commit, rootState }: any, machine: { id: number; state: string }) {
    if (
      !state.virtualMachines[rootState.currentWorkspace.id] ||
      !state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
    ) {
      return;
    }
    const index = state.virtualMachines[rootState.currentWorkspace.id][
      rootState.currentWorkspace.currentRegionId
    ].findIndex((item: any) => item.id === machine.id);
    if (index !== -1) {
      state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId][index].state =
        machine.state;
    }
    commit('setMachines', {
      data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
      workspaceId: rootState.currentWorkspace.id,
      regionId: rootState.currentWorkspace.currentRegionId,
    });
  },
  updateMachineNic({ state, commit, rootState }: any, payload: any) {
    if (
      !state.virtualMachines[rootState.currentWorkspace.id] ||
      !state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
    ) {
      return;
    }
    const index = state.virtualMachines[rootState.currentWorkspace.id][
      rootState.currentWorkspace.currentRegionId
    ].findIndex((item: any) => item.id === payload.virtual_machine.id);
    if (index !== -1) {
      const machine =
        state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId][index];

      let ipIndex = -1;

      if (machine.private_ip_addresses) {
        ipIndex = machine.private_ip_addresses.findIndex((item: any) => item.id === payload.id);
      } else {
        machine.private_ip_addresses = [];
      }

      if (ipIndex !== -1) {
        if (payload.modificationType === 'remove') {
          machine.private_ip_addresses.splice(ipIndex, 1);
        } else {
          machine.private_ip_addresses[ipIndex].ip_address = payload.ip_address;
        }
      } else if (payload.modificationType === 'add') {
        machine.private_ip_addresses.push({ id: payload.id, ip_address: payload.ip_address });
      }

      commit('setMachines', {
        data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
        workspaceId: rootState.currentWorkspace.id,
        regionId: rootState.currentWorkspace.currentRegionId,
      });
    }
  },
  updateMachineData({ state, commit, rootState }: any, machine: any) {
    if (
      !state.virtualMachines[rootState.currentWorkspace.id] ||
      !state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]
    ) {
      return;
    }
    const index = state.virtualMachines[rootState.currentWorkspace.id][
      rootState.currentWorkspace.currentRegionId
    ].findIndex((item: any) => item.id === machine.id);
    if (index !== -1) {
      state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId][index] = {
        ...state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId][index],
        ...machine,
      };
    }
    commit('setMachines', {
      data: state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId],
      workspaceId: rootState.currentWorkspace.id,
      regionId: rootState.currentWorkspace.currentRegionId,
    });
  },
};

const getters = {
  getMachines: (state: any, getters: any, rootState: any) => {
    if (!state.virtualMachines[rootState.currentWorkspace.id]) {
      return [];
    }
    if (!state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]) {
      return [];
    }
    return state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId];
  },
  getMachine: (state: any, getters: any, rootState: any) => (id: any) => {
    if (!state.virtualMachines[rootState.currentWorkspace.id]) {
      return null;
    }
    if (!state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId]) {
      return null;
    }
    return (
      state.virtualMachines[rootState.currentWorkspace.id][rootState.currentWorkspace.currentRegionId].find(
        (item: any) => item.id === id
      ) || null
    );
  },
  getWorkspaceMachine: (state: any, getters: any, rootState: any) => (
    machineId: any,
    workspaceId: any,
    regionId: any
  ) => {
    if (!state.virtualMachines[workspaceId]) {
      return null;
    }
    if (!state.virtualMachines[workspaceId][regionId]) {
      return null;
    }
    return state.virtualMachines[workspaceId][regionId].find((item: any) => item.id === machineId) || null;
  },
  getMachineHardware: (state: any, getters: any, rootState: any) => (id: any) => {
    return state.virtualMachineHardware[id] ? JSON.parse(JSON.stringify(state.virtualMachineHardware[id])) : null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
